/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { AppService } from '@services/app/app.service';
import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { ModalStateComponent } from './../modal-state/modal-state.component';
import { UiService } from '@services/ui/ui.service';
import { DestroyDirective } from '../destroy-abstract/destroy.abstract';
import { WizardService } from '@core/services/wizard/wizard.service';
import { takeUntil } from 'rxjs/operators';
import { slideInAnimation } from '@core/animations';
import { ContentService } from '@services/content/content.service';

@Component({
  selector: 'app-step-header',
  templateUrl: './step-header.component.html',
  styleUrls: ['./step-header.component.scss']
})
export class StepHeaderComponent
  extends DestroyDirective
  implements OnInit, OnChanges
{
  /**
   * disabilita i bottoni
   */
  @Input() disabled: boolean;

  /**
   * nasconde il bottone back
   */
  @Input() hideBack: boolean;

  /**
   * nasconde la barra di progresso
   */
  @Input() hideProgress: boolean;

  /**
   * titolo dell'header
   */
  @Input() title: string;

  /**
   * numero di step
   */
  @Input() totalSteps: number;

  /**
   * index step attivo
   */
  @Input() activeIndex = 0;

  /**
   * emette evento di back
   */
  @Output() back = new EventEmitter<void>();

  /**
   * nasconde bottone per modale di state
   */
  hideState: boolean;

  /**
   * url del logo
   */
  logoUrl: string;

  /**
   * flag per determinare la visualizzazione del logo
   */
  logoVisible: boolean;

  /**
   * flag che determina l'osservazione del wizard
   */
  absolute: boolean;

  /**
   * valore della barra
   */
  progress = 0;

  /**
   * lista di segments derivati dal numero di step impostati
   */
  segments: number[] = [];

  /**
   * messaggio di intro per la applicazione
   */
  appIntroMessage: string;

  constructor(
    private uiService: UiService,
    private appService: AppService,
    private wizardService: WizardService,
    private contentService: ContentService
  ) {
    super();
  }

  ngOnInit() {
    this.watchContentCode();
    this.readConfig();
    this.initSegmentsOrProgress();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.activeIndex || changes.totalSteps) {
      this.updateSegmentsOrProgress();
    }
  }

  /**
   * apre modale di state
   */
  async openState(): Promise<void> {
    await this.uiService.showModal<ModalStateComponent>({
      component: ModalStateComponent,
      cssClass: 'modal-state',
      enterAnimation: el => slideInAnimation(el, { direction: 'enter' }),
      leaveAnimation: el => slideInAnimation(el, { direction: 'leave' })
    });
  }

  /**
   * imposta messaggio di intro per la applicazione
   */
  private watchContentCode(): void {
    this.contentService
      .getContentCodeWhenChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe(code => {
        if (!!code) {
          this.appIntroMessage = `intro_${code}`;
        } else {
          this.appIntroMessage = 'intro';
        }
      });
  }

  /**
   * legge i valori del config
   */
  private readConfig(): void {
    const config = this.appService.getConfig();

    this.hideState = config.wizard.allowStateMenu !== true;
    this.logoUrl = config.theme.logoUrl;
    this.logoVisible = config.theme.logoOnHeader || false;
    this.absolute = config.wizard.progressBar === 'absolute';
  }

  /**
   * inizializza gli steps
   */
  private initSegmentsOrProgress(): void {
    if (!this.absolute) {
      this.updateSegments();
    } else {
      this.watchWizardAndUpdateProgress();
    }
  }

  /**
   * inizializza gli steps
   */
  private updateSegmentsOrProgress(): void {
    if (!this.absolute) {
      this.updateSegments();
    } else {
      this.updateProgress();
    }
  }

  /**
   * osserva i cambiamenti del wizard
   */
  private watchWizardAndUpdateProgress(): void {
    this.wizardService
      .getWizardWhenChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateProgress();
      });
  }

  /**
   * aggiorna il progress
   */
  private updateProgress(): void {
    this.progress = this.calcAbsoluteProgress() + this.calcRelativeProgress();
  }

  /**
   * aggiorna gli segments
   */
  private updateSegments(): void {
    this.segments = Array.from(Array(this.getRelativeStepsCount()).keys());
  }

  /**
   * calcola l'avanzamento rispetto al wizard
   */
  private calcAbsoluteProgress(): number {
    const stepIndex = this.getAbsoluteStepIndex();
    const increment = this.calcAbsoluteIncrement();

    return increment * stepIndex;
  }

  /**
   * calcola l'avanzamento rispetto ai sotto step
   */
  private calcRelativeProgress(): number {
    const stepIndex = this.getRelativeStepIndex();
    const increment = this.calcRelativeIncrement();

    return increment * stepIndex;
  }

  /**
   * calcola l'incremento per i sotto step
   */
  private calcRelativeIncrement(): number {
    const stepsCount = this.getRelativeStepsCount();

    if (stepsCount > 0) {
      return this.calcAbsoluteIncrement() / stepsCount;
    } else {
      return 0;
    }
  }

  /**
   * calcola l'incremento per il wizard
   */
  private calcAbsoluteIncrement(): number {
    const stepsCount = this.getAbsoluteStepsCount();

    if (stepsCount > 0) {
      return 100 / stepsCount;
    } else {
      return 0;
    }
  }

  private getAbsoluteStepIndex(): number {
    return this.numOrZero(this.wizardService.findActiveStepIndex());
  }

  private getAbsoluteStepsCount(): number {
    return this.numOrZero(this.wizardService.getWizardSteps().length);
  }

  private getRelativeStepIndex(): number {
    return this.numOrZero(this.activeIndex);
  }

  private getRelativeStepsCount(): number {
    return this.numOrZero(this.totalSteps);
  }

  private numOrZero(num: number): number {
    return isNaN(num) ? 0 : num;
  }
}
